import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button, Tag, message, Spin, Row, Col } from 'antd';
import { 
  EnvironmentOutlined, 
  DollarOutlined, 
  ClockCircleOutlined, 
  ShareAltOutlined,
  ArrowLeftOutlined,
  LinkedinOutlined,
  TwitterOutlined,
  LinkOutlined
} from '@ant-design/icons';
import { axiosInstant } from 'src/api/api';
import { useUser } from 'src/context/auth.context';
import { ROUTES } from '../common/constants';
import Navbar from '../NavBar';
import Footer from '../Footer';
import AuthFormModal from '../AuthPanel/AuthFormModal';
import { setPendingJobApplication, clearPendingJobApplication } from '../common/pendingJobApplication';
import styled from 'styled-components';

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f8fafb;
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const JobHeader = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const CompanyBadge = styled.div`
  background: linear-gradient(135deg, #efc73c 0%, #bb9638 100%);
  color: #333;
  padding: 6px 16px;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  display: inline-block;
  margin-bottom: 16px;
`;

const JobTitle = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 20px 0;
`;

const JobMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin-bottom: 24px;
  
  span {
    display: flex;
    align-items: center;
    gap: 8px;
    color: #666;
    font-size: 1rem;
  }
`;

const ShareSection = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 20px;
  border-top: 1px solid #f0f0f0;
`;

const ShareButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const JobSection = styled.div`
  background: white;
  border-radius: 16px;
  padding: 32px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 24px;
  padding-bottom: 14px;
  border-bottom: 3px solid #efc73c;
  display: inline-block;
`;

const DescriptionContent = styled.div`
  color: #3a3a3a;
  line-height: 1.8;
  font-size: 17px;
  
  h4 {
    margin: 32px 0 18px 0;
    color: #1a1a1a;
    font-weight: 700;
    font-size: 20px;
    letter-spacing: 0.3px;
  }
  
  p {
    margin: 16px 0;
    line-height: 1.8;
    font-size: 17px;
  }
  
  ul {
    margin: 18px 0 18px 28px;
    padding: 0;
    list-style-type: none;
  }
  
  li {
    position: relative;
    padding-left: 28px;
    margin-bottom: 12px;
    font-size: 17px;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 10px;
      width: 10px;
      height: 10px;
      background-color: #efc73c;
      border-radius: 50%;
    }
  }
`;

const ApplyButton = styled(Button)`
  background: linear-gradient(135deg, #efc73c 0%, #bb9638 100%);
  border: none;
  border-radius: 8px;
  height: 48px;
  font-weight: 600;
  font-size: 1.1rem;
  color: #333;
  width: 100%;
  
  &:hover {
    background: linear-gradient(135deg, #f5d55f 0%, #c9a045 100%);
    transform: translateY(-1px);
    color: #333;
  }
`;

const formatJobDescription = (description: string) => {
  if (!description) return null;
  
  const lines = description.split('\n');
  const elements: React.ReactNode[] = [];
  let currentList: string[] = [];
  let listKey = 0;
  
  const flushList = () => {
    if (currentList.length > 0) {
      elements.push(
        <ul key={`list-${listKey++}`}>
          {currentList.map((item, idx) => (
            <li key={idx}>{item}</li>
          ))}
        </ul>
      );
      currentList = [];
    }
  };
  
  lines.forEach((line, index) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flushList();
      return;
    }
    
    // Check if it's a section heading
    const headingPatterns = [
      /^(About\s*(Us|The\s*Company|The\s*Role)?|Company\s*Overview|Job\s*Description|Job\s*Responsibilities|Key\s*Responsibilities|Responsibilities|Requirements|Qualifications|Required\s*Qualifications|Preferred\s*Qualifications|What\s*You('ll|'ll|\s*Will)\s*(Do|Bring|Need)|What\s*We('re|'re|\s*Are)\s*Looking\s*For|Benefits|Perks|Skills|Required\s*Skills|Technical\s*Skills|Experience|Why\s*Join\s*Us|Our\s*Team|The\s*Opportunity|Role\s*Overview|Position\s*Summary|Summary|Overview|Who\s*You\s*Are|Your\s*Role|Your\s*Responsibilities|Nice\s*to\s*Have|Bonus\s*Points):?\s*$/i
    ];
    
    const isHeading = headingPatterns.some(pattern => pattern.test(trimmed)) || 
                      (trimmed.endsWith(':') && trimmed.length < 50 && !trimmed.match(/^[-•*]\s/));
    
    if (isHeading) {
      flushList();
      const headingText = trimmed.replace(/:$/, '');
      elements.push(<h4 key={`heading-${index}`}>{headingText}</h4>);
      return;
    }
    
    // Check if it's a bullet point
    const bulletMatch = trimmed.match(/^[-•*▪▸►◆]\s*(.+)/) || 
                        trimmed.match(/^\d+[.)]\s*(.+)/) ||
                        trimmed.match(/^[a-zA-Z][.)]\s*(.+)/);
    
    if (bulletMatch) {
      currentList.push(bulletMatch[1] || trimmed.replace(/^[-•*▪▸►◆]\s*/, '').replace(/^\d+[.)]\s*/, '').replace(/^[a-zA-Z][.)]\s*/, ''));
      return;
    }
    
    // Regular paragraph
    flushList();
    elements.push(<p key={`para-${index}`}>{trimmed}</p>);
  });
  
  flushList();
  return elements;
};

const JobDetailsPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const { user } = useUser();
  const [job, setJob] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [showLoginForm, setShowLoginForm] = useState(false);

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstant.get(`/jobs/${jobId}`);
      setJob(response.data);
    } catch (error) {
      console.error('Failed to fetch job details:', error);
      message.error('Failed to load job details');
    } finally {
      setLoading(false);
    }
  };

  const handleApply = async () => {
    // useUser() always returns a user object (empty when signed out), so the token
    // is what says whether anyone is actually signed in. Checking `!user` here
    // never matched, which sent signed-out visitors arriving from a shared job
    // link to /finishSignup - and straight back out to the landing page - instead
    // of opening the sign-in options.
    if (!user?.token) {
      // Parked so the job becomes a pending application once they finish signing up.
      setPendingJobApplication(jobId!);
      setShowLoginForm(true);
      return;
    }

    // Check if user is a candidate
    // A brand new user has no category until they finish signing up, so send them
    // there with the application still pending rather than telling them to switch
    // to a candidate account - which makes no sense to someone who just registered.
    if (!user?.category) {
      setPendingJobApplication(jobId!);
      navigate(ROUTES.FINISH_SIGNUP);
      return;
    }
    if (user.category !== 'candidate') {
      message.error('Only candidates can apply for jobs. Please switch to a candidate account.');
      return;
    }

    try {
      setApplying(true);
      await axiosInstant.post(`/job-applications/apply`, {
        jobId: jobId,
        candidateId: user._id,
      });
      message.success('Job added to your applications! Please complete your profile and answer screening questions to submit.');
      navigate(ROUTES.CANDIDATE_DASHBOARD);
    } catch (error: any) {
      console.error('Failed to apply:', error);
      if (error.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error('Failed to submit application');
      }
    } finally {
      setApplying(false);
    }
  };

  const shareJob = (platform: string) => {
    const url = window.location.href;
    const text = `Check out this job: ${job.positionTitle} at ${job.companyName}`;
    
    let shareUrl = '';
    switch (platform) {
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`;
        break;
      case 'copy':
        navigator.clipboard.writeText(url);
        message.success('Link copied to clipboard!');
        return;
    }
    
    if (shareUrl) {
      window.open(shareUrl, '_blank', 'width=600,height=400');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <Navbar />
        <ContentContainer style={{ textAlign: 'center', paddingTop: '100px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '20px', color: '#666' }}>Loading job details...</p>
        </ContentContainer>
        <Footer />
      </PageContainer>
    );
  }

  if (!job) {
    return (
      <PageContainer>
        <Navbar />
        <ContentContainer style={{ textAlign: 'center', paddingTop: '100px' }}>
          <h2>Job not found</h2>
          <Button type="primary" onClick={() => navigate('/job-board')}>
            Back to Job Board
          </Button>
        </ContentContainer>
        <Footer />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Navbar />
      <ContentContainer>
        <Button 
          icon={<ArrowLeftOutlined />} 
          onClick={() => navigate('/job-board')}
          style={{ marginBottom: '20px' }}
        >
          Back to Jobs
        </Button>

        <JobHeader>
          <CompanyBadge>{job.companyName}</CompanyBadge>
          <JobTitle>{job.positionTitle || job.title}</JobTitle>
          
          <JobMeta>
            {job.location && (
              <span>
                <EnvironmentOutlined style={{ color: '#efc73c', fontSize: '18px' }} />
                <strong>{job.location}</strong>
              </span>
            )}
            {job.workType && (
              <span>
                <ClockCircleOutlined style={{ color: '#efc73c', fontSize: '18px' }} />
                <strong>{job.workType}</strong>
              </span>
            )}
            {job.salary && (
              <span>
                <DollarOutlined style={{ color: '#efc73c', fontSize: '18px' }} />
                <strong>{job.salary}</strong>
              </span>
            )}
          </JobMeta>

          {job.workType?.toLowerCase() === 'remote' && (
            <Tag color="#52c41a" style={{ fontSize: '0.9rem', padding: '4px 12px' }}>
              Remote OK
            </Tag>
          )}

          <ShareSection>
            <ShareButton 
              icon={<LinkedinOutlined />} 
              onClick={() => shareJob('linkedin')}
            >
              Share on LinkedIn
            </ShareButton>
            <ShareButton 
              icon={<TwitterOutlined />} 
              onClick={() => shareJob('twitter')}
            >
              Share on Twitter
            </ShareButton>
            <ShareButton 
              icon={<LinkOutlined />} 
              onClick={() => shareJob('copy')}
            >
              Copy Link
            </ShareButton>
          </ShareSection>
        </JobHeader>

        {job.jobDescription && (
          <JobSection>
            <SectionTitle>Job Description</SectionTitle>
            <DescriptionContent>
              {formatJobDescription(job.jobDescription)}
            </DescriptionContent>
          </JobSection>
        )}

        {job.benefits && (
          <JobSection>
            <SectionTitle>Benefits & Perks</SectionTitle>
            <DescriptionContent>
              {formatJobDescription(job.benefits)}
            </DescriptionContent>
          </JobSection>
        )}

        {job.requiredSkills && job.requiredSkills.length > 0 && (
          <JobSection>
            <SectionTitle>Required Skills</SectionTitle>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {job.requiredSkills.map((skill: string, index: number) => (
                <Tag key={index} color="blue" style={{ fontSize: '0.9rem', padding: '4px 12px' }}>
                  {skill}
                </Tag>
              ))}
            </div>
          </JobSection>
        )}

        <JobSection>
          <ApplyButton 
            type="primary" 
            size="large" 
            onClick={handleApply}
            loading={applying}
          >
            {applying ? 'Submitting...' : 'Apply Now'}
          </ApplyButton>
        </JobSection>
      </ContentContainer>

      {showLoginForm && (
        <AuthFormModal
          onCancel={() => {
            clearPendingJobApplication();
            setShowLoginForm(false);
          }}
        />
      )}

      <Footer />
    </PageContainer>
  );
};

export default JobDetailsPage;

